/**
 * 69.59 step 3, the helicopter's weapons (DECISIONS 286 (2), (3); GameConfig.helicopters): look C's door guns and pods, firing on
 * their own through the gunship's weapon code (core/gunship.ts: what it sees to engage, its cones, its rounds). Each tick, while
 * a helicopter flies in, sits down or flies out:
 * - **the door gunners' .50s** (HELI.crew's two): each out of its own door, in bursts, at the nearest enemy in its cone within
 *   HELI_GUNS.reachPeople of the ground under it — a person on foot, a vehicle or a mount; the round MOUNTED_GUNS.hmg's;
 * - **the pods' unguided rockets** (the gunship's pods' class): a pair at a time at what lies ahead of its nose, on its run in.
 * 69.74 step 1: on station too — a helicopter's fire support flies the gunship's orbit (`loiter`), its left door on the inside.
 * Danger close as the gunship's: nothing with one of its own side within GUNSHIP.friendlyPeople. Broken off for home it holds its
 * fire (DECISIONS 277, as the gunship). Pure and deterministic (each round's scatter keyed by the seed, the flight, the weapon and
 * the round); with no helicopter armed nothing here runs.
 */
import type { Bullet, DropshipFlight, GameState, HeliLoad, HeliWeapon, Vec3 } from '../types';
import { HELI_GUNS } from '../constants/helicopter';
import { GUNSHIP_ROUNDS } from '../constants/gunship';
import { PHYS_DT } from '../constants';
import { vecAdd, vecCross, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { dcos, dsin } from './dmath';
import { SeededRandom } from './random';
import { flightNow } from './shipNow';
import { airstrikeOwner } from './airstrike';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { gravityAccel } from './physics';
import { mountedGunSpec } from './weapons';
import { personLineOfSight } from './fineTerrain';
import { bearingOf, inFrame, marksOf, nearest, round, type Mark } from './gunship';
import { defaultOn } from './defaultOn';

/** A shot a helicopter fired this tick (for the look and the sound): its flight, the weapon, where it left and where it was aimed. */
export interface HeliShot { flight: number; team: number; weapon: HeliWeapon; from: Vec3; to: Vec3 }

const ticksOf = (seconds: number): number => Math.max(1, Math.round(seconds / PHYS_DT));

/** A .50 from `from` at `to` (its fall allowed for, as the gunship's rounds), scattered by the gun's spread; a bullet the bullets' step flies. */
function fifty(state: GameState, id: number, team: number, ship: number, from: Vec3, to: Vec3, key: string): Bullet {
  const ps = personSetOf(state), w = mountedGunSpec(ps, 'hmg');
  const t = vecLength(vecSub(to, from)) / w.speed;
  const g = vecLength(gravityAccel(to, state.planets)) * ps.bulletGravityScale * ps.gravityScale;
  const up = vecNormalize(vecSub(to, state.planets[nearest(state, to)].position));
  let dir = vecNormalize(vecSub(vecAdd(to, vecScale(up, 0.5 * g * t * t)), from));
  const rng = new SeededRandom(key), a = rng.next() * 2 * Math.PI, sp = rng.next() * w.spread;
  const side = vecNormalize(vecCross(dir, up)), lift = vecCross(side, dir);
  dir = vecNormalize(vecAdd(dir, vecAdd(vecScale(side, dcos(a) * sp), vecScale(lift, dsin(a) * sp))));
  return { id, ownerId: airstrikeOwner(team), pos: from, vel: vecScale(dir, w.speed), age: 0, damage: w.damage, fromShip: ship,
    ...(w.life !== ps.bulletLife ? { life: w.life } : {}),
    ...(w.round && (defaultOn(state.config, 'hitLocations') || state.config.armour) ? { round: w.round } : {}),   // (185: its cartridge, where armour reads it)
    ...(w.drag ? { drag: w.drag } : {}) };
}

/**
 * One tick of every armed helicopter's fire: the rounds it fires made bullets, its load's counts and ticks moved on; the shots,
 * for the look. The same state when none fires.
 */
export function stepHeliGuns(state: GameState, tick: number): { state: GameState; shots: HeliShot[] } {
  const a = state.dropships;
  if (!a?.flights.some(f => f.heliGuns)) return { state, shots: [] };
  const shots: HeliShot[] = [], made: Bullet[] = [];
  let next = state.bulletsFired ?? 0;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k, seed = String(state.config.seed ?? '');
  const flights = a.flights.map((f): DropshipFlight => {
    const load = f.heliGuns;
    if (!load || f.evade?.kind === 'break') return f;   // (broken off for home, it holds its fire: the gunship's rule)
    const due = (w: HeliWeapon) => load.left[w] > 0 && tick >= load.next[w];
    if (!(['doorL', 'doorR', 'pods'] as const).some(due)) return f;
    const pose = flightNow(state, f, tick)?.flight?.pose;
    if (!pose || (pose.phase !== 'in' && pose.phase !== 'down' && pose.phase !== 'out' && pose.phase !== 'loiter')) return f;   // (69.74 step 1: on station)
    const L: HeliLoad = { left: { ...load.left }, next: { ...load.next }, burst: { ...load.burst } };
    const up = vecNormalize(pose.dir), left = vecCross(up, pose.heading);
    // what it sees to engage, nearest the ground under it first (once: each gun's sight from its own muzzle below)
    const marks = marksOf(state, f, inFrame(pose, [0, 1.0, 0], person), { centre: pose.dir, reachPeople: HELI_GUNS.reachPeople, slantPeople: HELI_GUNS.slantPeople, sight: false });
    const rescan = ticksOf(HELI_GUNS.scanSeconds);
    const shoot = (w: HeliWeapon, from: Vec3, b: Bullet, to: Vec3) => { made.push(b); L.left[w]--; shots.push({ flight: f.id, team: f.team, weapon: w, from, to }); };
    const key = (w: HeliWeapon) => `${seed}-heli-${f.id}-${w}-${L.left[w]}`;
    // the door guns: each out of its own door (its outward normal: the left's +x, the right's −x), its gunner's burst at the nearest in its cone
    for (const [w, i] of [['doorL', 0], ['doorR', 1]] as const) {
      if (!due(w)) continue;
      const from = inFrame(pose, HELI_GUNS.muzzles.door[i], person), C = HELI_GUNS.cones.door, out = i === 0 ? left : vecScale(left, -1);
      const inCone = (q: Mark) => { const b = bearingOf(pose, from, q.at, out); return b.off <= C.bearing && b.down >= C.depression[0] && b.down <= C.depression[1]; };
      const m = marks.find(q => inCone(q) && personLineOfSight(from, q.at, state));
      if (!m) { L.next[w] = tick + rescan; continue; }   // (nothing on its side: it looks again in a moment)
      shoot(w, from, fifty(state, next++, f.team, f.id, from, m.at, key(w)), m.at);
      L.burst[w]--;
      L.next[w] = tick + ticksOf(L.burst[w] > 0 ? mountedGunSpec(personSetOf(state), 'hmg').interval : HELI_GUNS.doorRest);
      if (L.burst[w] <= 0) L.burst[w] = HELI_GUNS.doorBurst;
    }
    // the pods: a pair at what lies ahead of its nose, on its run in (and round its orbit on station), unguided
    if (due('pods') && (pose.phase === 'in' || pose.phase === 'loiter')) {
      const C = HELI_GUNS.cones.pods, ahead = inFrame(pose, [0, 1.36, 0.6], person);
      const m = marks.find(q => { const b = bearingOf(pose, ahead, q.at, pose.heading); return b.off <= C.bearing && b.down >= C.depression[0] && b.down <= C.depression[1] && personLineOfSight(ahead, q.at, state); });
      if (!m) L.next.pods = tick + rescan;
      else {
        for (const i of [0, 1] as const) {
          if (L.left.pods <= 0) break;
          const from = inFrame(pose, HELI_GUNS.muzzles.pods[i], person);
          shoot('pods', from, { ...round(state, next++, f.team, from, m.at, GUNSHIP_ROUNDS.pods, key('pods'), 0), fromShip: f.id }, m.at);
        }
        L.next.pods = tick + ticksOf(GUNSHIP_ROUNDS.pods.interval);
      }
    }
    return L.left.doorL === load.left.doorL && L.left.doorR === load.left.doorR && L.left.pods === load.left.pods && L.next.doorL === load.next.doorL && L.next.doorR === load.next.doorR && L.next.pods === load.next.pods ? f : { ...f, heliGuns: L };
  });
  if (!made.length && flights.every((f, i) => f === a.flights[i])) return { state, shots };
  if (!made.length) return { state: { ...state, dropships: { ...a, flights } }, shots };
  return { state: { ...state, dropships: { ...a, flights }, bullets: [...(state.bullets ?? []), ...made], bulletsFired: next }, shots };
}
